/**
 * Strict TypeScript Type Definitions
 *
 * This file contains all type definitions for the CRM application
 * with strict typing to prevent any types and ensure type safety.
 */

import type { ReactNode, MouseEvent, ChangeEvent, FocusEvent } from "react";

// ============================================================================
// API TYPES
// ============================================================================

/**
 * Generic API response wrapper
 */
export interface ApiResponse<T> {
  pagination: any;
  data: T;
  message?: string;
  success: boolean;
  errors?: Record<string, string[]>;
}

/**
 * Paginated response wrapper
 */
export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * API error response
 */
export interface ApiErrorResponse {
  message: string;
  code: string;
  status: number;
  errors?: Record<string, string[]>;
}

/**
 * API request configuration
 */
export interface ApiRequestConfig {
  skipAuth?: boolean;
  skipErrorHandling?: boolean;
  retryCount?: number;
  headers?: Record<string, string>;
  params?: Record<string, string | number | boolean>;
  data?: unknown;
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  url?: string;
}

// ============================================================================
// AUTHENTICATION TYPES
// ============================================================================

/**
 * JWT Payload interface
 */
export interface JWTPayload {
  sub?: string;
  iss?: string;
  aud?: string;
  exp?: number;
  iat?: number;
  nbf?: number;
  jti?: string;
  [key: string]: unknown;
}

/**
 * Token data interface
 */
export interface TokenData {
  accessToken: string;
  refreshToken?: string;
  expiresIn: number;
}

/**
 * User interface
 */
export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatar?: string;
  phone?: string;
  company?: string;
  jobTitle?: string;
  bio?: string;
  timezone?: string;
  language?: string;
  role: string;
  roles: string[];
  isEmailVerified: boolean;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Login credentials
 */
export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

/**
 * Sign up data
 */
export interface SignUpData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phone?: string;
}

/**
 * Profile update data
 */
export interface ProfileUpdateData {
  firstName?: string;
  lastName?: string;
  phone?: string;
  company?: string;
  jobTitle?: string;
  bio?: string;
  timezone?: string;
  language?: string;
}

/**
 * Authentication response
 */
export interface AuthResponse {
  user: User;
  token: string;
  refreshToken: string;
  expiresIn: number;
  redirectUrl?: string;
}

/**
 * Auth request types (for API)
 */
export interface LoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface SignUpRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phone?: string;
  acceptMarketing?: boolean;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
  confirmPassword: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface VerifyEmailRequest {
  token: string;
}

export interface ResendVerificationRequest {
  email: string;
}

/**
 * Customer types (for API)
 */
export interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  position?: string;
  address?: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  status: "active" | "inactive" | "prospect" | "lead";
  source: string;
  tags: string[];
  notes?: string;
  assignedTo?: string;
  lastContact?: string;
  totalRevenue?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCustomerRequest {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  position?: string;
  address?: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  status?: "active" | "inactive" | "prospect" | "lead";
  source?: string;
  tags?: string[];
  notes?: string;
  assignedTo?: string;
}

export interface UpdateCustomerRequest extends Partial<CreateCustomerRequest> {
  id: string;
}

export interface CustomerFilters {
  search?: string;
  status?: string[];
  source?: string[];
  tags?: string[];
  assignedTo?: string;
  dateFrom?: string;
  dateTo?: string;
  minRevenue?: number;
  maxRevenue?: number;
}

export interface CustomerListParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  filters?: CustomerFilters;
}

export interface CustomerStats {
  total: number;
  active: number;
  inactive: number;
  prospects: number;
  leads: number;
  totalRevenue: number;
  averageRevenue: number;
  newThisMonth: number;
  newThisWeek: number;
}

export interface CustomerImportRequest {
  file: File;
  mapping: Record<string, string>;
  options: {
    skipDuplicates: boolean;
    updateExisting: boolean;
    notifyOnCompletion: boolean;
  };
}

export interface CustomerExportRequest {
  format: "csv" | "xlsx" | "pdf";
  filters?: CustomerFilters;
  fields?: string[];
}

// ============================================================================
// FORM AND VALIDATION TYPES
// ============================================================================

/**
 * Form field types
 */
export type FormFieldType =
  | "text"
  | "email"
  | "password"
  | "number"
  | "select"
  | "textarea"
  | "checkbox"
  | "radio"
  | "date"
  | "file"
  | "tel"
  | "url";

/**
 * Validation rule interface
 */
export interface ValidationRule {
  test: (value: unknown) => boolean;
  message: string;
  code: string;
}

/**
 * Validation error interface
 */
export interface ValidationErrorData {
  field: string;
  message: string;
  code: string;
}

/**
 * Form data interface
 */
export interface FormData {
  [key: string]: unknown;
}

/**
 * Form field configuration
 */
export interface FormField {
  name: string;
  type: FormFieldType;
  label: string;
  placeholder?: string;
  required?: boolean;
  validation?: ValidationRule[];
  options?: Array<{ value: string; label: string }>;
}

// ============================================================================
// COMPONENT PROPS TYPES
// ============================================================================

/**
 * Base component props
 */
export interface BaseComponentProps {
  className?: string;
  id?: string;
  "data-testid"?: string;
}

/**
 * Button props
 */
export interface ButtonProps extends BaseComponentProps {
  variant?: "primary" | "secondary" | "danger" | "ghost" | "outline";
  size?: "sm" | "md" | "lg";
  disabled?: boolean;
  loading?: boolean;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  children: ReactNode;
  type?: "button" | "submit" | "reset";
}

/**
 * Input props
 */
export interface InputProps extends BaseComponentProps {
  type?: "text" | "email" | "password" | "number" | "tel" | "url";
  value?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  name?: string;
  autoComplete?: string;
}

/**
 * Select props
 */
export interface SelectProps extends BaseComponentProps {
  value?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  onChange?: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  name?: string;
}

/**
 * Modal props
 */
export interface ModalProps extends BaseComponentProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
}

/**
 * Card props
 */
export interface CardProps extends BaseComponentProps {
  title?: string;
  children: ReactNode;
  actions?: ReactNode;
  loading?: boolean;
}

// ============================================================================
// EVENT HANDLER TYPES
// ============================================================================

/**
 * Form event handlers
 */
export type FormSubmitHandler<T> = (data: T) => void | Promise<void>;
export type FormChangeHandler<T> = (field: keyof T, value: T[keyof T]) => void;
export type FormBlurHandler<T> = (field: keyof T) => void;

/**
 * Input event handlers
 */
export type InputChangeHandler = (value: string) => void;
export type InputFocusHandler = () => void;
export type InputBlurHandler = () => void;

/**
 * Button event handlers
 */
export type ButtonClickHandler = (event: MouseEvent<HTMLButtonElement>) => void;

/**
 * Generic event handlers
 */
export type ChangeEventHandler<T = Element> = (event: ChangeEvent<T>) => void;
export type ClickEventHandler<T = Element> = (event: MouseEvent<T>) => void;
export type FocusEventHandler<T = Element> = (event: FocusEvent<T>) => void;
export type BlurEventHandler<T = Element> = (event: FocusEvent<T>) => void;

// ============================================================================
// REDUX TYPES
// ============================================================================

/**
 * Root state interface
 */
export interface RootState {
  auth: AuthState;
  theme: ThemeState;
  i18n: I18nState;
  sidebar: SidebarState;
  ui: UIState;
}

/**
 * Auth state interface
 */
export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

/**
 * Theme state interface
 */
export interface ThemeState {
  theme: "light" | "dark" | "system";
}

/**
 * I18n state interface
 */
export interface I18nState {
  language: "en" | "es" | "fr" | "de" | "ar";
  fallbackLanguage: "en" | "es" | "fr" | "de" | "ar";
}

/**
 * Sidebar state interface
 */
export interface SidebarState {
  collapsed: boolean;
}

/**
 * UI state interface
 */
export interface UIState {
  isLoading: boolean;
  notifications: Notification[];
  modals: Record<string, boolean>;
}

/**
 * Notification interface
 */
export interface Notification {
  id: string;
  type: "info" | "success" | "warning" | "error";
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
}

// ============================================================================
// UTILITY TYPES
// ============================================================================

/**
 * Type utilities
 */
export type PartialUser = Partial<User>;
export type RequiredUser = {
  [K in keyof User]-?: User[K];
};
export type UserBasicInfo = Pick<
  User,
  "id" | "firstName" | "lastName" | "email"
>;
export type UserWithoutPassword = Omit<User, "password">;

/**
 * API response type extraction
 */
export type ApiResponseType<T> =
  T extends Promise<ApiResponse<infer U>> ? U : never;

/**
 * Optional fields utility
 */
export type Optional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

/**
 * Required fields utility
 */
export type RequiredFields<T, K extends keyof T> = T & { [P in K]-?: T[P] };

/**
 * Deep partial utility
 */
export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

// ============================================================================
// TYPE GUARDS
// ============================================================================

/**
 * Type guard for API errors
 */
export const isApiError = (error: unknown): error is ApiError => {
  return error instanceof Error && "status" in error && "code" in error;
};

/**
 * Type guard for validation errors
 */
export const isValidationError = (error: unknown): error is ValidationError => {
  return (
    typeof error === "object" &&
    error !== null &&
    "field" in error &&
    "message" in error
  );
};

/**
 * Type guard for user objects
 */
export const isUser = (obj: unknown): obj is User => {
  return (
    typeof obj === "object" && obj !== null && "id" in obj && "email" in obj
  );
};

/**
 * Type guard for JWT payload
 */
export const isJWTPayload = (obj: unknown): obj is JWTPayload => {
  return typeof obj === "object" && obj !== null && "exp" in obj;
};

// ============================================================================
// TESTING TYPES
// ============================================================================

/**
 * Mock data types
 */
export interface MockUser extends User {
  password?: string;
}

/**
 * Test props types
 */
export interface TestComponentProps {
  "data-testid"?: string;
  mockData?: unknown;
}

/**
 * Test utility types
 */
export type RenderResult = any; // Testing library types
export type Screen = any; // Testing library types

/**
 * Factory function types
 */
export type UserFactory = (overrides?: Partial<User>) => User;
export type ApiResponseFactory<T> = (data: T) => ApiResponse<T>;

// ============================================================================
// ERROR TYPES
// ============================================================================

/**
 * Custom API error class
 */
export class ApiError extends Error {
  public status: number;
  public code: string;
  public errors?: Record<string, string[]>;
  public isNetworkError: boolean;

  constructor(
    message: string,
    status: number,
    code: string,
    errors?: Record<string, string[]>,
    isNetworkError = false,
  ) {
    super(message);
    this.status = status;
    this.code = code;
    this.errors = errors;
    this.isNetworkError = isNetworkError;
  }
}

/**
 * Validation error class
 */
export class ValidationError extends Error {
  public field: string;
  public code: string;

  constructor(field: string, message: string, code: string) {
    super(message);
    this.field = field;
    this.code = code;
  }
}

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Error codes
 */
export const ERROR_CODES = {
  INVALID_FORMAT: "INVALID_FORMAT",
  REQUIRED_FIELD: "REQUIRED_FIELD",
  MIN_LENGTH: "MIN_LENGTH",
  MAX_LENGTH: "MAX_LENGTH",
  INVALID_EMAIL: "INVALID_EMAIL",
  INVALID_PHONE: "INVALID_PHONE",
  INVALID_URL: "INVALID_URL",
  INVALID_NUMBER: "INVALID_NUMBER",
  INVALID_DATE: "INVALID_DATE",
  INVALID_CHOICE: "INVALID_CHOICE",
  NETWORK_ERROR: "NETWORK_ERROR",
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  NOT_FOUND: "NOT_FOUND",
  VALIDATION_ERROR: "VALIDATION_ERROR",
  RATE_LIMIT: "RATE_LIMIT",
  SERVER_ERROR: "SERVER_ERROR",
  UNKNOWN_ERROR: "UNKNOWN_ERROR",
} as const;

/**
 * HTTP status codes
 */
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  UNPROCESSABLE_ENTITY: 422,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
} as const;

// Environment types
export interface ImportMetaEnv {
  readonly VITE_API_URL?: string;
  readonly VITE_APP_NAME?: string;
  readonly VITE_APP_VERSION?: string;
  readonly MODE: string;
  readonly DEV: boolean;
  readonly PROD: boolean;
}

export interface ImportMeta {
  readonly env: ImportMetaEnv;
}

// Node.js types for browser environment
declare global {
  namespace NodeJS {
    interface ProcessEnv {
      NODE_ENV: "development" | "production" | "test";
      VITE_API_URL?: string;
      VITE_APP_NAME?: string;
      VITE_APP_VERSION?: string;
      VITE_DOMAIN?: string;
      VITE_SUBDOMAIN?: string;
      VITE_BACKEND_API_BASE_URL?: string;
      VITE_API_TIMEOUT?: string;
    }
  }
}
